/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import * as _ from 'lodash';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/heatmap';

import template from './heatmap.component.html';
import {calculateDatesAsWeeks} from '../controller/allocation-calculator';

@Component({
  standalone: false,
  selector: 'adn-heatmap',
  template
})
export class HeatmapComponent implements OnChanges, OnDestroy {
  @Input() triggerRedraw: any;
  @Input() stats: any[];
  @Input() dates: any[];
  @Input() isPending: boolean;
  @Input() noDataMessage: string;

  haveStats = false;
  noData = false;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.triggerRedraw || !this.triggerRedraw) {
      this.triggerRedraw = this.triggerRedraw || {};
      this.triggerRedraw.execute = () => this.updateChartState();
    }
    this.updateChartState();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private updateChartState(): void {
    this.noData = !_.isArray(this.stats) || this.stats.length === 0;
    this.chartOptions = this.noData ? null : this.createChartOptions();
    this.haveStats = true;
    this.renderChart();
  }

  private createChartOptions(): any {
    const dates = this.dates;
    const weekLabels = calculateDatesAsWeeks(this.dates);
    return {
      chart: {
        type: 'heatmap',
        plotBorderWidth: 1
      },
      title: {
        text: ''
      },
      xAxis: {
        categories: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        min: 0,
        max: 6,
        gridLineWidth: 1
      },
      yAxis: {
        categories: weekLabels,
        reversed: true,
        title: null,
        min: 0,
        gridLineWidth: 1
      },
      legend: {
        align: 'right',
        layout: 'vertical',
        margin: 0,
        verticalAlign: 'top'
      },
      colorAxis: {
        min: 0,
        minColor: '#FFFFFF',
        maxColor: Highcharts.getOptions().colors[0]
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      tooltip: {
        formatter: function() {
          return '<b>' + dates[this.point.index] + '</b>';
        }
      },
      series: [
        {
          borderWidth: 1,
          data: this.stats,
          dataLabels: {
            enabled: true,
            color: '#000000'
          }
        }
      ]
    };
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || this.noData) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
